import { UserPermissionsMap } from 'src/app/services/api-interface.service';
import { NavItem } from './nav-item/nav-item';
import { adminItems } from './admin';
import { nonAdminItems } from './non-admin';

/**
 * Roles that receive the full admin sidebar (matches login API `user.role`, case-insensitive).
 * Do not use substring checks like `includes('admin')` — e.g. "NonAdmin" would match incorrectly.
 */
const ADMIN_ROLE_NAMES = new Set(
  [
    'admin',
    'administrator',
    'super admin',
    'superadmin',
    'system administrator',
  ].map((r) => r.toLowerCase())
);

/** Login / stored user object shape from API (subset). */
export interface LoginUserPayload {
  id?: string;
  email?: string;
  full_name?: string;
  role?: string;
  role_id?: string;
  hubs?: unknown[];
  is_superuser?: boolean;
  is_staff?: boolean;
  permissions?: UserPermissionsMap;
}

export function isAdminRoleName(name: string | null | undefined): boolean {
  return ADMIN_ROLE_NAMES.has(String(name ?? '').trim().toLowerCase());
}

export function isAdminSidebarUser(user: LoginUserPayload | null | undefined): boolean {
  if (!user) return false;
  if (user.is_superuser === true) return true;
  return isAdminRoleName(user.role);
}

function canSeeNavItem(item: NavItem, user: LoginUserPayload): boolean {
  if (item.navCap) return true;
  if (!item.permission) return true;
  if (isAdminSidebarUser(user)) return true;
  const flags = user.permissions?.[item.permission];
  const action = item.action || 'view';
  return !!flags?.[`can_${action}`];
}

function pruneNavCaps(items: NavItem[]): NavItem[] {
  const result: NavItem[] = [];
  items.forEach((item, index) => {
    if (!item.navCap) {
      result.push(item);
      return;
    }
    const hasFollowing = items.slice(index + 1).some((next) => {
      if (next.navCap) return false;
      return true;
    });
    if (hasFollowing) result.push(item);
  });
  return result.filter((item, index, list) => {
    if (!item.navCap) return true;
    const next = list[index + 1];
    return !!next && !next.navCap;
  });
}

function filterNavByPermissions(items: NavItem[], user: LoginUserPayload): NavItem[] {
  const filtered: NavItem[] = [];
  for (const item of items) {
    if (item.navCap) {
      filtered.push(item);
      continue;
    }
    if (item.children?.length) {
      const children = item.children.filter((child) => canSeeNavItem(child, user));
      if (!children.length) continue;
      filtered.push({ ...item, children });
      continue;
    }
    if (canSeeNavItem(item, user)) filtered.push(item);
  }
  return pruneNavCaps(filtered);
}

export function getSidebarNavItemsForUser(user: LoginUserPayload | null | undefined): NavItem[] {
  if (!user) return [];
  if (!user.permissions) {
    return isAdminSidebarUser(user) ? adminItems : nonAdminItems;
  }
  return filterNavByPermissions(adminItems, user);
}

export function readStoredLoginUser(): LoginUserPayload | null {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    return JSON.parse(raw) as LoginUserPayload;
  } catch {
    return null;
  }
}

export function getSidebarNavItemsForStoredUser(): NavItem[] {
  return getSidebarNavItemsForUser(readStoredLoginUser());
}
